import create from 'zustand';
import { immer } from 'zustand/middleware/immer';

import tradeAPI from 'lib/api/trade';
import useUIConfigStore from 'stores/useUIConfigStore';
import { AcceptancesPayload } from 'global/Securities/types/api/termsAndConditions';
import {
  FeatureAcceptance,
  TnCFeatureIDEnum,
  TnCFeaturesIDEnum,
  FeatureAcceptances,
  TnCFeaturePersistMap,
  SubAccountType,
  SubAccountStatus,
} from '../types/termsAndCondition';
import {
  getTnCByFeature,
  postTnCAcceptance,
  getTnCByFeatures,
  postTnCAcceptances,
} from '../api/termsAndConditions';

interface State {
  contents: FeatureAcceptance;
  isSubmitting: boolean;

  // terms and condition v2
  termsData: FeatureAcceptances;
  accountStatus: string;
}

interface Actions {
  loadTnC(
    featureId: TnCFeatureIDEnum,
    onAgreementPending?: () => void,
    onAgreementAccepted?: () => void,
  ): Promise<void>;
  postAcceptance(featureId: TnCFeatureIDEnum, version: number): Promise<void>;

  // terms and condition v2
  loadTnCAcceptance(featureIds: TnCFeaturesIDEnum[]): Promise<void | boolean>;
  postAcceptances(acceptances: AcceptancesPayload[]): Promise<void | boolean>;
  loadSubAccountStatus(type: SubAccountType): Promise<void | boolean>;
}

const initialState: State = {
  contents: undefined,
  isSubmitting: false,
  termsData: undefined,
  accountStatus: '',
};

const useTermsAndConditionStore = create(
  immer<State & Actions>((set) => ({
    ...initialState,

    async loadTnC(featureId, onAgreementPending, onAgreementAccepted) {
      try {
        const response = await getTnCByFeature(featureId);
        const { data } = response.data;
        /**
         * About the `isAccepted` value
         *
         * If the acceptance letters response contain no result,
         * it means user is already accepted the Terms and Conditions.
         *
         * References:
         * See RFC 0127 TNC Management on [GET] Terms and condition letters
         */
        const isAccepted = data.acceptance_letters.length === 0;
        set((draft) => {
          draft.contents = {
            ...draft.contents,
            [featureId]: {
              isAccepted,
              acceptanceLetters: data.acceptance_letters,
            },
          };
        });

        if (!isAccepted) {
          onAgreementPending();
          return;
        }
        onAgreementAccepted();
      } catch (error) {
        Promise.reject(error);
      }
    },

    async postAcceptance(featureId, version) {
      try {
        set((draft) => {
          draft.isSubmitting = true;
        });
        await postTnCAcceptance(featureId, version, true);

        set((draft) => {
          draft.contents[featureId].isAccepted = true;
          draft.isSubmitting = false;
        });
      } catch (error) {
        set((draft) => {
          draft.contents[featureId].isAccepted = false;
          draft.isSubmitting = false;
        });
        Promise.reject(error);
      }
    },

    // terms and condition v2
    async loadTnCAcceptance(featureIds) {
      try {
        const response = await getTnCByFeatures(featureIds);
        const { data } = response.data;
        const acceptanceLetters = data.acceptance_letters;

        acceptanceLetters.forEach((letter) => {
          set((draft) => {
            draft.termsData = {
              ...draft.termsData,
              [letter.feature_id]: {
                isAccepted: letter.is_accepted,
                url: letter.url,
                version: letter.version,
              },
            };
          });

          const storeKey = TnCFeaturePersistMap[letter.feature_id];
          useUIConfigStore.getState().setUIValue(storeKey, letter.is_accepted);
        });

        const isDayTradeAccepted = useUIConfigStore
          .getState()
          .getUIValue('dayTradeTnc');
        return isDayTradeAccepted;
      } catch (error) {
        Promise.reject(error);
        return false;
      }
    },

    async postAcceptances(acceptances) {
      set((draft) => {
        draft.isSubmitting = true;
      });
      try {
        await postTnCAcceptances(acceptances);

        set((draft) => {
          draft.isSubmitting = false;
        });
        return true;
      } catch (error) {
        set((draft) => {
          draft.isSubmitting = false;
        });
        Promise.reject(error);
        return false;
      } finally {
        set((draft) => {
          draft.isSubmitting = false;
        });
      }
    },

    async loadSubAccountStatus(type) {
      try {
        const response = await tradeAPI.checkSubAccountDayTrade(type);
        const { data: { sub_account } = {} } = response.data;
        const { status } = sub_account || {};

        set((draft) => {
          draft.accountStatus = status;
        });

        const isActive = status === SubAccountStatus.ACTIVE;
        const storeKey = 'dayTradeSubAccountActive';
        useUIConfigStore.getState().setUIValue(storeKey, isActive);

        return isActive;
      } catch (error) {
        Promise.reject(error);
        // default behaviour
        return true;
      }
    },
  })),
);

export default useTermsAndConditionStore;
